"use client";

import Image from "next/image";
import { geoDistance, geoGraticule10, geoOrthographic, geoPath } from "d3-geo";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { feature } from "topojson-client";
import type { FeatureCollection, Geometry } from "geojson";
import type { GeometryCollection, Topology } from "topojson-specification";
import worldAtlas from "world-atlas/countries-110m.json";
import usFlag from "flag-icons/flags/4x3/us.svg";
import phFlag from "flag-icons/flags/4x3/ph.svg";
import ghFlag from "flag-icons/flags/4x3/gh.svg";

type DeliveryMarketKey = "us" | "ph" | "gh";

type DeliveryMarket = {
  key: DeliveryMarketKey;
  number: string;
  country: string;
  cities: string;
  role: string;
  strength: string;
  languages: string;
  coverage: string;
  color: string;
  rgb: string;
  flag: string;
  flagAlt: string;
  coordinates: Array<[number, number]>;
  center: [number, number];
};

const markets: DeliveryMarket[] = [
  {
    key: "us",
    number: "01",
    country: "United States",
    cities: "McAllen, Texas",
    role: "Client proximity and program alignment.",
    strength: "Market context, leadership access and customer coordination.",
    languages: "English · Spanish",
    coverage: "North America programs and global client coordination.",
    color: "#f6921e",
    rgb: "246,146,30",
    flag: usFlag,
    flagAlt: "Flag of the United States.",
    coordinates: [[26.2034, -98.23]],
    center: [26.2034, -98.23],
  },
  {
    key: "ph",
    number: "02",
    country: "Philippines",
    cities: "Iloilo",
    role: "Established service delivery and deep operational expertise.",
    strength: "Experienced customer operations and multi-channel execution.",
    languages: "English",
    coverage: "Global programs and extended-hour delivery.",
    color: "#6fcfff",
    rgb: "111,207,255",
    flag: phFlag,
    flagAlt: "Flag of the Philippines.",
    coordinates: [[10.7202, 122.5621]],
    center: [10.7202, 122.5621],
  },
  {
    key: "gh",
    number: "03",
    country: "Ghana",
    cities: "Accra",
    role: "Strategic GMT alignment and deep talent capacity for rapid scaling.",
    strength: "Adaptability, service execution and responsive program support.",
    languages: "English · French",
    coverage: "Global programs and flexible coverage windows.",
    color: "#9cd9bb",
    rgb: "156,217,187",
    flag: ghFlag,
    flagAlt: "Flag of Ghana.",
    coordinates: [[5.6037, -0.187]],
    center: [5.6037, -0.187],
  },
];

type Atlas = Topology<{ countries: GeometryCollection }>;

const atlas = worldAtlas as unknown as Atlas;
const geography = feature(atlas, atlas.objects.countries) as FeatureCollection<Geometry>;
type GlobeCenter = [number, number];

const geographicLightPoints: Array<{
  coordinates: [number, number];
  tone: "cyan" | "warm";
  radius: number;
}> = [
  { coordinates: [47.6062, -122.3321], tone: "cyan", radius: 1.7 },
  { coordinates: [37.7749, -122.4194], tone: "warm", radius: 1.5 },
  { coordinates: [32.7767, -96.797], tone: "cyan", radius: 1.45 },
  { coordinates: [41.8781, -87.6298], tone: "warm", radius: 1.7 },
  { coordinates: [40.7128, -74.006], tone: "warm", radius: 2.1 },
  { coordinates: [25.7617, -80.1918], tone: "cyan", radius: 1.45 },
  { coordinates: [19.4326, -99.1332], tone: "warm", radius: 1.7 },
  { coordinates: [4.711, -74.0721], tone: "cyan", radius: 1.25 },
  { coordinates: [-23.5505, -46.6333], tone: "warm", radius: 1.9 },
  { coordinates: [-34.6037, -58.3816], tone: "cyan", radius: 1.3 },
  { coordinates: [51.5072, -0.1276], tone: "warm", radius: 1.8 },
  { coordinates: [48.8566, 2.3522], tone: "cyan", radius: 1.5 },
  { coordinates: [52.52, 13.405], tone: "warm", radius: 1.35 },
  { coordinates: [6.5244, 3.3792], tone: "cyan", radius: 1.45 },
  { coordinates: [-26.2041, 28.0473], tone: "warm", radius: 1.45 },
  { coordinates: [25.2048, 55.2708], tone: "cyan", radius: 1.5 },
  { coordinates: [19.076, 72.8777], tone: "warm", radius: 1.8 },
  { coordinates: [1.3521, 103.8198], tone: "cyan", radius: 1.7 },
  { coordinates: [35.6762, 139.6503], tone: "warm", radius: 1.8 },
  { coordinates: [-33.8688, 151.2093], tone: "cyan", radius: 1.5 },
];

const interpolateLongitude = (from: number, to: number, progress: number) => {
  const distance = ((to - from + 540) % 360) - 180;
  return from + distance * progress;
};

const createGeographicView = (center: GlobeCenter) => {
  const projection = geoOrthographic()
    .translate([500, 470])
    .scale(405)
    .clipAngle(90)
    .precision(0.25)
    .rotate([-center[1], -center[0], 0]);
  const path = geoPath(projection);
  const graticulePath = path(geoGraticule10()) ?? "";
  const landPath = path(geography) ?? "";
  const projectedMarkets = markets.map((market) => {
    const [latitude, longitude] = market.center;
    const projectedCenter = projection([longitude, latitude]) ?? [500, 470];
    const visible = geoDistance(
      [center[1], center[0]],
      [longitude, latitude],
    ) < Math.PI / 2 - 0.015;
    const locations = market.coordinates.map(([locationLatitude, locationLongitude]) => ({
      point: projection([locationLongitude, locationLatitude]) ?? projectedCenter,
      visible: geoDistance(
        [center[1], center[0]],
        [locationLongitude, locationLatitude],
      ) < Math.PI / 2 - 0.015,
    }));
    return { market, center: projectedCenter, visible, locations };
  });
  const projectedLightPoints = geographicLightPoints.map(({ coordinates, tone, radius }) => {
    const [latitude, longitude] = coordinates;
    return {
      point: projection([longitude, latitude]) ?? [500, 470],
      visible: geoDistance([center[1], center[0]], [longitude, latitude]) < Math.PI / 2 - 0.035,
      tone,
      radius,
    };
  });
  return { graticulePath, landPath, projectedMarkets, projectedLightPoints };
};

const staticGeographicView = createGeographicView(markets[0].center);

function GeographicSvgGlobe({
  activeMarket,
  interactive,
  onPreview,
  onSelect,
}: {
  activeMarket: DeliveryMarket;
  interactive: boolean;
  onPreview: (key: DeliveryMarketKey | null) => void;
  onSelect: (key: DeliveryMarketKey) => void;
}) {
  const [orientation, setOrientation] = useState<GlobeCenter>(activeMarket.center);
  const orientationRef = useRef<GlobeCenter>(activeMarket.center);

  useEffect(() => {
    const from = orientationRef.current;
    const to = activeMarket.center;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reducedMotion) {
      const frame = window.requestAnimationFrame(() => {
        orientationRef.current = to;
        setOrientation(to);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    let frame = 0;
    const startedAt = performance.now();
    const duration = 380;
    const animate = (time: number) => {
      const progress = Math.min(1, (time - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next: GlobeCenter = [
        from[0] + (to[0] - from[0]) * eased,
        interpolateLongitude(from[1], to[1], eased),
      ];
      orientationRef.current = next;
      setOrientation(next);
      if (progress < 1) frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [activeMarket]);

  const calculatedView = useMemo(() => createGeographicView(orientation), [orientation]);
  const geographicView = calculatedView.landPath.length > 100
    ? calculatedView
    : staticGeographicView;

  if (calculatedView.landPath.length <= 100 && process.env.NODE_ENV !== "production") {
    console.error("[GlobalDeliveryNetwork] Local geographic data produced an empty SVG path; using the bundled static geographic view.");
  }

  return (
    <svg
      className="delivery-geographic-globe"
      data-dataset="world-atlas-countries-110m"
      data-orientation={activeMarket.key}
      viewBox="0 0 1000 1000"
      preserveAspectRatio="xMidYMid meet"
      role="group"
      aria-label="Interactive geographic globe showing FTO delivery locations in the United States, the Philippines and Ghana."
    >
      <defs>
        <radialGradient id="deliveryGeographicOcean" cx="35%" cy="30%" r="72%">
          <stop offset="0" stopColor="#0d4771" />
          <stop offset="0.43" stopColor="#052d55" />
          <stop offset="0.78" stopColor="#021a36" />
          <stop offset="1" stopColor="#010b1a" />
        </radialGradient>
        <linearGradient id="deliveryGeographicShade" x1="20%" y1="16%" x2="86%" y2="88%">
          <stop offset="0" stopColor="#d2f1ff" stopOpacity="0.12" />
          <stop offset="0.47" stopColor="#009f66" stopOpacity="0.025" />
          <stop offset="0.74" stopColor="#010c1c" stopOpacity="0.24" />
          <stop offset="1" stopColor="#000" stopOpacity="0.72" />
        </linearGradient>
        <filter id="deliveryGeographicEdge" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="11" />
        </filter>
        <filter id="deliveryGeographicHubHalo" x="-120%" y="-120%" width="340%" height="340%">
          <feGaussianBlur stdDeviation="14" />
        </filter>
        <pattern id="deliveryGeographicLandMesh" width="13" height="13" patternUnits="userSpaceOnUse">
          <circle cx="2.5" cy="2.5" r="0.9" fill="#b8ecff" fillOpacity="0.34" />
          <circle cx="9" cy="8" r="0.55" fill="#f3dbc0" fillOpacity="0.2" />
        </pattern>
        <clipPath id="deliveryGeographicClip">
          <circle cx="500" cy="470" r="405" />
        </clipPath>
      </defs>

      <g aria-hidden="true">
        <circle className="delivery-geographic-atmosphere" cx="500" cy="470" r="414" />
        <circle cx="500" cy="470" r="405" fill="url(#deliveryGeographicOcean)" />
      </g>
      <g clipPath="url(#deliveryGeographicClip)" aria-hidden="true">
        <path className="delivery-geographic-graticule" d={geographicView.graticulePath} />
        <path className="delivery-geographic-land" d={geographicView.landPath} />
        <path className="delivery-geographic-land-mesh" d={geographicView.landPath} fill="url(#deliveryGeographicLandMesh)" />
        <path className="delivery-geographic-boundaries" d={geographicView.landPath} />
        <g className="delivery-geographic-density">
          {geographicView.projectedLightPoints.map(({ point, visible, tone, radius }, index) => (
            <circle
              cx={point[0]}
              cy={point[1]}
              r={radius}
              data-visible={visible ? "true" : "false"}
              data-tone={tone}
              key={`${tone}-${index}`}
            />
          ))}
        </g>
        <circle cx="500" cy="470" r="405" fill="url(#deliveryGeographicShade)" />
        {geographicView.projectedMarkets.flatMap(({ market, locations }) => locations.map(({ point, visible }, locationIndex) => (
          <g
            className="delivery-geographic-location"
            data-current={market.key === activeMarket.key ? "true" : "false"}
            data-visible={visible ? "true" : "false"}
            style={{ "--hub-accent": market.color, "--hub-rgb": market.rgb } as CSSProperties}
            transform={`translate(${point[0]} ${point[1]})`}
            key={`${market.key}-${locationIndex}`}
          >
            <circle className="delivery-geographic-location-halo" r="58" />
            <circle className="delivery-geographic-location-pulse" r="24" />
            <circle r={market.key === activeMarket.key ? 14 : 9} />
            <circle r={market.key === activeMarket.key ? 4 : 3} />
          </g>
        )))}
      </g>
      <circle className="delivery-geographic-edge" cx="500" cy="470" r="405" aria-hidden="true" />

      {geographicView.projectedMarkets.map(({ market, center, visible }) => (
        <g
          className="delivery-geographic-control"
          data-current={market.key === activeMarket.key ? "true" : "false"}
          data-visible={visible ? "true" : "false"}
          style={{ "--hub-accent": market.color, "--hub-rgb": market.rgb } as CSSProperties}
          transform={`translate(${center[0]} ${center[1]})`}
          role="button"
          tabIndex={interactive && visible ? 0 : -1}
          aria-label={`Explore ${market.country} delivery capabilities.`}
          aria-pressed={market.key === activeMarket.key}
          onClick={() => onSelect(market.key)}
          onFocus={() => onPreview(market.key)}
          onBlur={() => onPreview(null)}
          onPointerEnter={(event) => { if (event.pointerType === "mouse") onPreview(market.key); }}
          onPointerLeave={(event) => { if (event.pointerType === "mouse") onPreview(null); }}
          onKeyDown={(event) => {
            if (event.key !== "Enter" && event.key !== " ") return;
            event.preventDefault();
            onSelect(market.key);
          }}
          key={market.key}
        >
          <circle className="delivery-geographic-hit" r="44" />
          <circle className="delivery-geographic-control-ring" r={market.key === activeMarket.key ? 28 : 22} />
          <circle className="delivery-geographic-control-core" r={market.key === activeMarket.key ? 8 : 6} />
          <g className="delivery-geographic-label" transform={market.key === "gh" ? "translate(-44 -10)" : "translate(44 -10)"}>
            <text className="delivery-geographic-number" textAnchor={market.key === "gh" ? "end" : "start"}>
              {market.number}
            </text>
            <text className="delivery-geographic-country" y="21" textAnchor={market.key === "gh" ? "end" : "start"}>
              {market.country.toUpperCase()}
            </text>
            <text className="delivery-geographic-city" y="42" textAnchor={market.key === "gh" ? "end" : "start"}>
              {market.cities}
            </text>
          </g>
        </g>
      ))}
    </svg>
  );
}

function DeliveryGlobe({
  activeMarket,
  enabled,
  onPreview,
  onSelect,
}: {
  activeMarket: DeliveryMarket;
  enabled: boolean;
  onPreview: (key: DeliveryMarketKey | null) => void;
  onSelect: (key: DeliveryMarketKey) => void;
}) {
  return (
    <div
      className="delivery-globe-stage"
      data-enabled={enabled ? "true" : "false"}
      data-render-status="ready"
      data-renderer="svg-2.5d"
    >
      <div className="delivery-globe-aura" aria-hidden="true" />
      <GeographicSvgGlobe
        activeMarket={activeMarket}
        interactive={enabled}
        onPreview={onPreview}
        onSelect={onSelect}
      />
      <span className="delivery-globe-caption">FTO / GLOBAL DELIVERY NETWORK</span>
    </div>
  );
}

export default function GlobalDeliveryNetwork({ active }: { active: boolean }) {
  const [selectedMarket, setSelectedMarket] = useState<DeliveryMarketKey>("us");
  const [previewMarket, setPreviewMarket] = useState<DeliveryMarketKey | null>(null);
  const selectorRefs = useRef<Record<DeliveryMarketKey, HTMLButtonElement | null>>({
    us: null,
    ph: null,
    gh: null,
  });
  const activeMarket = useMemo(
    () => markets.find(({ key }) => key === (previewMarket ?? selectedMarket)) ?? markets[0],
    [previewMarket, selectedMarket],
  );

  const preview = (key: DeliveryMarketKey | null) => setPreviewMarket(key);
  const select = (key: DeliveryMarketKey) => {
    setSelectedMarket(key);
    setPreviewMarket(null);
  };

  return (
    <section
      className="global-delivery-network"
      data-active={active ? "true" : "false"}
      style={{ "--delivery-accent": activeMarket.color, "--delivery-rgb": activeMarket.rgb } as CSSProperties}
      aria-label="The FTO Global Delivery Network"
    >
      <div className="delivery-globe-column">
        <DeliveryGlobe
          activeMarket={activeMarket}
          enabled={active}
          onPreview={preview}
          onSelect={select}
        />
      </div>

      <div className="delivery-information">
        <div className="delivery-market-selector" role="tablist" aria-label="FTO delivery markets" aria-orientation="vertical">
          {markets.map((market, index) => (
            <button
              type="button"
              role="tab"
              id={`delivery-tab-${market.key}`}
              aria-controls="delivery-market-panel"
              aria-selected={selectedMarket === market.key}
              tabIndex={selectedMarket === market.key ? 0 : -1}
              data-current={activeMarket.key === market.key ? "true" : "false"}
              ref={(node) => { selectorRefs.current[market.key] = node; }}
              onClick={() => select(market.key)}
              onFocus={() => preview(market.key)}
              onBlur={() => preview(null)}
              onPointerEnter={(event) => { if (event.pointerType === "mouse") preview(market.key); }}
              onPointerLeave={(event) => {
                if (event.pointerType === "mouse" && document.activeElement !== event.currentTarget) preview(null);
              }}
              onKeyDown={(event) => {
                if (!["ArrowUp", "ArrowDown", "Home", "End"].includes(event.key)) return;
                event.preventDefault();
                const nextIndex = event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? markets.length - 1
                    : (index + (event.key === "ArrowDown" ? 1 : -1) + markets.length) % markets.length;
                const nextMarket = markets[nextIndex];
                preview(nextMarket.key);
                selectorRefs.current[nextMarket.key]?.focus();
              }}
              key={market.key}
            >
              <span>{market.number}</span>
              <strong>{market.country}</strong>
              <i aria-hidden="true" />
            </button>
          ))}
        </div>

        <article
          id="delivery-market-panel"
          className="delivery-market-card"
          role="tabpanel"
          aria-live="polite"
          aria-labelledby={`delivery-tab-${activeMarket.key}`}
          key={activeMarket.key}
        >
          <header>
            <div><span>{activeMarket.number}</span><strong>{activeMarket.country}</strong><p>{activeMarket.cities}</p></div>
            <span className="delivery-country-flag">
              <Image src={activeMarket.flag} alt={activeMarket.flagAlt} width={64} height={48} unoptimized />
            </span>
          </header>
          <div className="delivery-market-facts">
            <section><small>Why This Location</small><p>{activeMarket.role}</p></section>
            <section><small>Operating Strength</small><p>{activeMarket.strength}</p></section>
            <section><small>Languages</small><p>{activeMarket.languages}</p></section>
            <section><small>Coverage</small><p>{activeMarket.coverage}</p></section>
          </div>
        </article>
      </div>

      <p className="one-standard-line">
        <strong>One FTO Standard</strong><span>Experience Design</span><i />
        <span>Quality Discipline</span><i /><span>Operational Visibility</span><i /><span>Human Accountability</span>
      </p>
    </section>
  );
}
